import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Users, Award, DollarSign, Link2, Copy, Plus, Eye,
  TrendingUp, CheckCircle2, Clock, RefreshCw, Percent,
  ChevronRight, ExternalLink, Edit2, Trash2, Mail,
  FileSignature, Tag, Calendar, ArrowRight, BarChart3, X
} from "lucide-react";

// ==================== CREATE ADVOCATE DIALOG ====================
function CreateAdvocateDialog({ isOpen, onClose, onSuccess, subCategoryId, category }) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", company: "",
    commission_rate: "10", discount_percent: "15",
    trial_offer_days: "60", type: "advocate",
    notes: "", contract_terms: "",
    pilot_types: []
  });
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // All pilot types available
  const PILOT_TYPES = [
    { key: "makeup", label: "Makeup" },
    { key: "hair", label: "Hair" },
    { key: "nail", label: "Nail" },
    { key: "lash", label: "Lash" },
    { key: "events", label: "Events" },
    { key: "wellness", label: "Wellness" },
    { key: "culinary", label: "Culinary" },
    { key: "instructor", label: "Instructor" }
  ];

  // Search existing users
  const searchExistingUsers = async (q) => {
    if (!q || q.length < 2) { setUserSearchResults([]); return; }
    setSearchingUsers(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/users/search?q=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setUserSearchResults(data.users || []); }
    } catch {}
    finally { setSearchingUsers(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => { if (userSearchQuery) searchExistingUsers(userSearchQuery); }, 300);
    return () => clearTimeout(t);
  }, [userSearchQuery]);

  const selectExistingUser = (user) => {
    setSelectedUser(user);
    setFormData({
      ...formData,
      name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      company: user.company_name || user.company || "",
    });
    setUserSearchQuery("");
    setUserSearchResults([]);
    toast.success(`Filled from ${user.first_name || user.name || user.email}`);
  };

  const togglePilotType = (type) => {
    const current = formData.pilot_types || [];
    const updated = current.includes(type) 
      ? current.filter(t => t !== type)
      : [...current, type];
    setFormData({ ...formData, pilot_types: updated });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/brand-advocates`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...formData,
          sub_category_id: subCategoryId,
          pilot_types: formData.pilot_types.length > 0 ? formData.pilot_types : [category?.toLowerCase()],
          linked_user_id: selectedUser?.user_id,
          commission_rate: parseFloat(formData.commission_rate),
          discount_percent: parseFloat(formData.discount_percent),
          trial_offer_days: parseInt(formData.trial_offer_days),
        })
      });
      if (res.ok) {
        toast.success("Advocate invited!");
        onSuccess();
        onClose();
        setFormData({ name: "", email: "", phone: "", company: "", commission_rate: "10", discount_percent: "15", trial_offer_days: "60", type: "advocate", notes: "", contract_terms: "", pilot_types: [] });
        setSelectedUser(null);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create advocate");
      }
    } catch { toast.error("Error creating advocate"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="w-5 h-5" /> Invite Brand Advocate
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1">
          <div className="space-y-4 py-2 pr-2">
            {/* Search Existing Users */}
            <div>
              <Label>Search Existing People</Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  value={userSearchQuery} 
                  onChange={e => setUserSearchQuery(e.target.value)} 
                  placeholder="Search by name or email..." 
                  className="pl-10" 
                  data-testid="advocate-user-search"
                />
              </div>
              {userSearchResults.length > 0 && (
                <div className="mt-1 border rounded-lg max-h-32 overflow-y-auto bg-white shadow-sm">
                  {userSearchResults.map((u, i) => (
                    <button key={u.user_id || i} onClick={() => selectExistingUser(u)} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 border-b last:border-0">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs font-medium">
                        {(u.first_name || u.name || "U").charAt(0).toUpperCase()}
                      </div>
                      <div><p className="font-medium">{u.first_name || u.name || "Unknown"} {u.last_name || ""}</p><p className="text-xs text-gray-500">{u.email}</p></div>
                    </button>
                  ))}
                </div>
              )}
              {searchingUsers && <p className="text-xs text-gray-400 mt-1">Searching...</p>}
              {selectedUser && (
                <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200 flex items-center justify-between">
                  <span className="text-sm text-green-700">Linked to: {selectedUser.email}</span>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)} className="text-green-600 h-6 px-2">
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>

            <div className="border-t pt-3">
              <p className="text-xs text-gray-500 mb-3">Or fill in manually:</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Name *</Label>
                  <Input value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Advocate name" data-testid="advocate-name" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="advocate@example.com" data-testid="advocate-email" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="+1 555 000 0000" />
              </div>
              <div>
                <Label>Company</Label>
                <Input value={formData.company} onChange={e => setFormData(p => ({ ...p, company: e.target.value }))} placeholder="Business name" />
              </div>
            </div>

            {/* Pilot Types - Multi-select */}
            <div>
              <Label>Pilot Categories</Label>
              <p className="text-xs text-gray-500 mb-2">Select categories this advocate will represent (can be multiple)</p>
              <div className="flex flex-wrap gap-2">
                {PILOT_TYPES.map(type => {
                  const isSelected = (formData.pilot_types || []).includes(type.key);
                  return (
                    <button
                      key={type.key}
                      type="button"
                      onClick={() => togglePilotType(type.key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        isSelected 
                          ? "bg-purple-500 text-white" 
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      data-testid={`pilot-type-${type.key}`}
                    >
                      {type.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Advocate Type</Label>
              <Select value={formData.type} onValueChange={v => setFormData(p => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="advocate-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="advocate">Brand Advocate (Referral Only)</SelectItem>
                  <SelectItem value="provider_advocate">Provider Advocate (Uses Phygital + Refers)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.type === "provider_advocate" 
                  ? "Provider who also advocates  -  earns from referrals + gets discount code"
                  : "External advocate  -  earns commission on every referred paying user for life"}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Commission %</Label>
                <Input type="number" min="1" max="50" value={formData.commission_rate} onChange={e => setFormData(p => ({ ...p, commission_rate: e.target.value }))} />
                <p className="text-xs text-gray-400 mt-0.5">Of monthly fee, for life</p>
              </div>
              <div>
                <Label>Discount %</Label>
                <Input type="number" min="0" max="50" value={formData.discount_percent} onChange={e => setFormData(p => ({ ...p, discount_percent: e.target.value }))} />
                <p className="text-xs text-gray-400 mt-0.5">Off referral's plan</p>
              </div>
              <div>
                <Label>Trial Days</Label>
                <Input type="number" min="0" max="180" value={formData.trial_offer_days} onChange={e => setFormData(p => ({ ...p, trial_offer_days: e.target.value }))} />
                <p className="text-xs text-gray-400 mt-0.5">Free trial for referrals</p>
              </div>
            </div>

            <div>
              <Label>Contract Terms (optional)</Label>
              <Textarea
                value={formData.contract_terms}
                onChange={e => setFormData(p => ({ ...p, contract_terms: e.target.value }))}
                rows={3}
                placeholder="Custom terms... Default: Lifetime revenue share on referred paying users."
              />
            </div>

            <div>
              <Label>Internal Notes</Label>
              <Textarea value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Notes about this advocate..." />
            </div>
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="create-advocate-btn">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Advocate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== ADVOCATE DETAIL MODAL ====================
function AdvocateDetailModal({ advocate, isOpen, onClose, categoryColors, onRefresh, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  if (!advocate) return null;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/refer/${advocate.tracking_code}`);
    toast.success("Referral link copied!");
  };
  const handleCopyCode = () => {
    navigator.clipboard.writeText(advocate.discount_code || "");
    toast.success("Discount code copied!");
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A";

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/brand-advocates/${advocate.advocate_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editData)
      });
      if (res.ok) {
        toast.success("Advocate updated");
        setEditing(false);
        onRefresh();
      } else { toast.error("Failed to update"); }
    } catch { toast.error("Error saving"); }
    finally { setSaving(false); }
  };

  const handleSignContract = async () => {
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/admin/brand-advocates/${advocate.advocate_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sign_contract: true })
      });
      toast.success("Contract marked as signed!");
      onRefresh();
    } catch { toast.error("Error signing contract"); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full ${categoryColors.bg} text-white flex items-center justify-center text-lg font-semibold`}>
              {advocate.name?.charAt(0).toUpperCase() || "A"}
            </div>
            <div>
              <DialogTitle className="text-left">{advocate.name}</DialogTitle>
              <p className="text-sm text-gray-500">{advocate.email}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge className={advocate.type === "provider_advocate" ? "bg-indigo-500" : "bg-purple-500"}>
                  {advocate.type === "provider_advocate" ? "Provider Advocate" : "Brand Advocate"}
                </Badge>
                {/* Show pilot types */}
                {(advocate.pilot_types || []).map(pt => (
                  <Badge key={pt} variant="secondary" className="text-xs capitalize">{pt}</Badge>
                ))}
              </div>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1">
          <div className="space-y-5 py-2">
            {/* Pilot Categories */}
            {(advocate.pilot_types || []).length > 0 && (
              <div className="bg-purple-50 rounded-lg p-3">
                <p className="text-xs font-medium text-purple-700 mb-2">Represents Categories:</p>
                <div className="flex flex-wrap gap-2">
                  {advocate.pilot_types.map(pt => (
                    <span key={pt} className="px-3 py-1 rounded-full bg-purple-200 text-purple-800 text-xs font-medium capitalize">{pt}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              <Card>
                <CardContent className="pt-3 pb-3 text-center">
                  <Users className="w-4 h-4 mx-auto mb-1 text-gray-400" />
                  <p className="text-xl font-bold">{advocate.referrals_count || 0}</p>
                  <p className="text-xs text-gray-500">Referrals</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3 text-center">
                  <CheckCircle2 className="w-4 h-4 mx-auto mb-1 text-green-500" />
                  <p className="text-xl font-bold">{advocate.conversions_count || 0}</p>
                  <p className="text-xs text-gray-500">Conversions</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-3 pb-3 text-center">
                  <DollarSign className="w-4 h-4 mx-auto mb-1 text-amber-500" />
                  <p className="text-xl font-bold">${advocate.earnings_total || 0}</p>
                  <p className="text-xs text-gray-500">Earnings</p>
                </CardContent>
              </Card>
            </div>

            {/* Referral Link & Discount Code */}
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500 mb-1.5">Referral Link</p>
                <div className="flex gap-2">
                  <Input readOnly value={`${window.location.origin}/refer/${advocate.tracking_code || "..."}`} className="bg-white text-sm" />
                  <Button size="sm" onClick={handleCopyLink}><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500 mb-1.5">Discount Code</p>
                <div className="flex gap-2">
                  <Input readOnly value={advocate.discount_code || "N/A"} className="bg-white text-sm font-mono" />
                  <Button size="sm" onClick={handleCopyCode}><Copy className="w-4 h-4" /></Button>
                </div>
                <p className="text-xs text-gray-400 mt-1">{advocate.discount_percent || 15}% off for referred users</p>
              </div>
            </div>

            {/* Deal Structure */}
            <Card>
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Percent className="w-4 h-4" /> Deal Structure
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Commission</p>
                    <p className="font-medium">{advocate.commission_rate || 10}% of monthly fee</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Duration</p>
                    <p className="font-medium">Lifetime per referral</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Referral Discount</p>
                    <p className="font-medium">{advocate.discount_percent || 15}% off</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Free Trial</p>
                    <p className="font-medium">{advocate.trial_offer_days || 60} days</p>
                  </div>
                </div>
                <div className="pt-2 border-t text-xs text-gray-400">
                  Strategy: {advocate.trial_offer_days || 60}-day free trial &rarr; NFC reader setup in-person &rarr; Upsell to monthly/quarterly/yearly
                </div>
              </CardContent>
            </Card>

            {/* Contract */}
            <Card>
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileSignature className="w-4 h-4" /> Contract
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Badge className={advocate.contract?.signed ? "bg-green-500" : "bg-amber-500"}>
                      {advocate.contract?.signed ? "Signed" : "Pending Signature"}
                    </Badge>
                    {advocate.contract?.signed_at && (
                      <p className="text-xs text-gray-400 mt-1">Signed {formatDate(advocate.contract.signed_at)}</p>
                    )}
                  </div>
                  {!advocate.contract?.signed && (
                    <Button size="sm" variant="outline" onClick={handleSignContract}>
                      <FileSignature className="w-4 h-4 mr-1" /> Mark as Signed
                    </Button>
                  )}
                </div>
                <p className="text-sm text-gray-600 bg-gray-50 rounded p-3">
                  {advocate.contract?.terms || "Standard Brand Advocate Agreement - Lifetime revenue share on referred paying users."}
                </p>
              </CardContent>
            </Card>

            {/* Notes & Meta */}
            <div className="space-y-2 text-sm">
              {advocate.notes && (
                <div>
                  <p className="text-gray-500 text-xs">Notes</p>
                  <p className="bg-gray-50 rounded p-2">{advocate.notes}</p>
                </div>
              )}
              <div className="text-xs text-gray-400 flex justify-between">
                <span>Created: {formatDate(advocate.created_at)}</span>
                <span>Pending Payout: ${advocate.pending_payout || 0}</span>
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="border-t pt-3 flex justify-between">
          <Button variant="outline" size="sm" className="text-red-500" onClick={() => { onDelete(advocate.advocate_id); onClose(); }}>
            <Trash2 className="w-4 h-4 mr-1" /> Remove
          </Button>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== MAIN TAB ====================
export default function BrandAdvocatesTab({ subCategoryData, categoryColors, category, subCategoryId, onRefresh }) {
  const [advocates, setAdvocates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedAdvocate, setSelectedAdvocate] = useState(null);
  const [forecast, setForecast] = useState(null);

  useEffect(() => { fetchAdvocates(); fetchForecast(); }, [subCategoryId]);

  const fetchAdvocates = async () => {
    if (!subCategoryId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/brand-advocates?sub_category_id=${subCategoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAdvocates(data.advocates || []);
      }
    } catch (err) { console.error("Failed to fetch advocates:", err); }
    finally { setLoading(false); }
  };

  const fetchForecast = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/revenue-forecast`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setForecast(await res.json());
    } catch {}
  };

  const handleDeleteAdvocate = async (advocateId) => {
    if (!window.confirm("Remove this advocate?")) return;
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/admin/brand-advocates/${advocateId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Advocate removed");
      fetchAdvocates();
    } catch { toast.error("Error removing advocate"); }
  };

  const totalReferrals = advocates.reduce((a, b) => a + (b.referrals_count || 0), 0);
  const totalConversions = advocates.reduce((a, b) => a + (b.conversions_count || 0), 0);
  const totalEarnings = advocates.reduce((a, b) => a + (b.earnings_total || 0), 0);
  const conversionRate = totalReferrals > 0 ? Math.round((totalConversions / totalReferrals) * 100) : 0;

  return (
    <div className="p-6 space-y-6" data-testid="brand-advocates-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Award className="w-5 h-5" /> Brand Advocates
          </h3>
          <p className="text-sm text-gray-500">Invite-only advocates with lifetime rev share, discount codes, and referral tracking</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className={categoryColors.bg} data-testid="invite-advocate-btn">
          <Plus className="w-4 h-4 mr-2" /> Invite Advocate
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Advocates", value: advocates.length, icon: Award, color: categoryColors },
          { label: "Total Referrals", value: totalReferrals, icon: Link2, color: { text: "text-blue-600", bg: "bg-blue-500" } },
          { label: "Conversion Rate", value: `${conversionRate}%`, icon: TrendingUp, color: { text: "text-green-600", bg: "bg-green-500" } },
          { label: "Total Commissions", value: `$${totalEarnings}`, icon: DollarSign, color: { text: "text-amber-600", bg: "bg-amber-500" } },
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <Card key={i} className={i === 0 ? `${categoryColors.light} ${categoryColors.border} border` : ""}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-2xl font-bold ${stat.color.text || ""}`}>{stat.value}</p>
                    <p className="text-sm text-gray-600">{stat.label}</p>
                  </div>
                  <Icon className={`w-8 h-8 ${stat.color.text || categoryColors.text} opacity-40`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Advocate List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Active Advocates</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : advocates.length === 0 ? (
            <div className="text-center py-12">
              <Award className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No advocates yet</p>
              <p className="text-sm text-gray-400 mt-1">Invite brand advocates to grow through referrals</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Invite First Advocate
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {advocates.map((adv) => (
                <div
                  key={adv.advocate_id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => setSelectedAdvocate(adv)}
                  data-testid={`advocate-card-${adv.advocate_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${categoryColors.bg} text-white flex items-center justify-center font-medium`}>
                      {adv.name?.charAt(0).toUpperCase() || "A"}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{adv.name}</p>
                        <Badge className={adv.type === "provider_advocate" ? "bg-indigo-500 text-xs" : "bg-purple-500 text-xs"}>
                          {adv.type === "provider_advocate" ? "Provider" : "Advocate"}
                        </Badge>
                        {/* Show pilot types */}
                        {(adv.pilot_types || []).length > 1 && (
                          <Badge variant="secondary" className="text-xs">{adv.pilot_types.length} categories</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{adv.email}</p>
                      {(adv.pilot_types || []).length > 0 && (
                        <p className="text-xs text-gray-400 capitalize">{adv.pilot_types.join(", ")}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm font-mono font-medium">{adv.discount_code}</p>
                      <p className="text-xs text-gray-500">discount code</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{adv.referrals_count || 0} refs</p>
                      <p className="text-xs text-gray-500">{adv.commission_rate || 10}% rate</p>
                    </div>
                    <Badge className={adv.contract?.signed ? "bg-green-500" : "bg-amber-500"}>
                      {adv.contract?.signed ? "Contracted" : "Pending"}
                    </Badge>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revenue Forecast */}
      {forecast && (
        <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-white">
              <BarChart3 className="w-5 h-5" /> Revenue Forecast
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              {Object.entries(forecast.scenarios || {}).map(([name, data]) => (
                <div key={name} className="bg-white/10 rounded-lg p-4">
                  <p className="text-xs text-gray-300 uppercase mb-2">{name}</p>
                  <p className="text-2xl font-bold">${data.mrr?.toLocaleString()}</p>
                  <p className="text-xs text-gray-300">MRR ({Math.round(data.conversion_rate * 100)}% convert)</p>
                  <div className="mt-2 text-xs text-gray-400">
                    <p>{data.breakdown?.solo || 0} Solo · {data.breakdown?.team || 0} Team · {data.breakdown?.luxe || 0} Luxe</p>
                    <p className="mt-1">ARR: ${data.arr?.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-400 border-t border-white/10 pt-3">
              <span>Trial Members: {forecast.total_trial_members || 0}</span>
              <span>NFC Revenue Est: ${forecast.nfc_revenue?.toLocaleString() || 0}</span>
              <span>Active Advocates: {forecast.total_advocates || 0}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Strategy Card */}
      <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500 text-white flex items-center justify-center shrink-0">
              <Percent className="w-6 h-6" />
            </div>
            <div>
              <h4 className="font-semibold text-purple-900">Revenue Sharing Strategy</h4>
              <div className="text-sm text-purple-700 mt-2 space-y-1">
                <p><ArrowRight className="w-3 h-3 inline mr-1" /> 2-month free trial with NFC card reader setup in-person</p>
                <p><ArrowRight className="w-3 h-3 inline mr-1" /> Upsell to Solo ($11/mo), Team ($44/mo), or Luxe ($111/mo)</p>
                <p><ArrowRight className="w-3 h-3 inline mr-1" /> Advocates earn lifetime % on each referred paying user</p>
                <p><ArrowRight className="w-3 h-3 inline mr-1" /> Discount codes drive trial sign-ups, recurring revenue builds</p>
              </div>
              <div className="flex gap-4 mt-3 text-xs text-purple-600">
                <span><strong>Default Rate:</strong> 10%</span>
                <span><strong>Payout Threshold:</strong> $50</span>
                <span><strong>Schedule:</strong> Monthly</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <CreateAdvocateDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={fetchAdvocates}
        subCategoryId={subCategoryId}
        category={category}
      />

      <AdvocateDetailModal
        advocate={selectedAdvocate}
        isOpen={!!selectedAdvocate}
        onClose={() => setSelectedAdvocate(null)}
        categoryColors={categoryColors}
        onRefresh={() => { fetchAdvocates(); fetchForecast(); }}
        onDelete={handleDeleteAdvocate}
      />
    </div>
  );
}
